import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import './Auth.css';
import GoogleSignInButton from './GoogleSignInButton';
import CookieConsentBanner from './CookieConsentBanner';
import { TERMS_OF_USE, PRIVACY_POLICY } from '../../content/legalDocuments';


function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // ✅ Legal modals
  const [openModal, setOpenModal] = useState(null); // null | "terms" | "privacy"

  const { login, user } = useAuth();
  const navigate = useNavigate();

  const isModalOpen = openModal !== null;

  // Redirect to dashboard if already logged in
  useEffect(() => {
    if (user) {
      navigate('/dashboard', { replace: true });
    }
  }, [user, navigate]);

  // ✅ Close on ESC + lock scroll while modal open
  useEffect(() => {
    if (!isModalOpen) return;

    const onKeyDown = (e) => {
      if (e.key === 'Escape') setOpenModal(null);
    };

    document.addEventListener('keydown', onKeyDown);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [isModalOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await login(email, password);

    if (result.success) {
      navigate('/dashboard');
    } else {
      setError(result.error);
    }

    setLoading(false);
  };

  const Modal = ({ title, children }) => {
    if (!isModalOpen) return null;

    return (
      <div
        className="legal-modal-overlay"
        role="presentation"
        onMouseDown={(e) => {
          // Click outside closes
          if (e.target === e.currentTarget) setOpenModal(null);
        }}
      >
        <div
          className="legal-modal"
          role="dialog"
          aria-modal="true"
          aria-label={title}
        >
          <div className="legal-modal-header">
            <h3>{title}</h3>
            <button
              type="button"
              className="legal-modal-close"
              onClick={() => setOpenModal(null)}
              aria-label="Close"
            >
              ✕
            </button>
          </div>

          <div className="legal-modal-body">
            {children}
          </div>

          <div className="legal-modal-footer">
            <button
              type="button"
              className="btn-primary legal-modal-ok"
              onClick={() => setOpenModal(null)}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <h1>Welcome to The Ascended</h1>
        <h2>Login</h2>

        <div style={{ margin: '20px 0', textAlign: 'center' }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            margin: '20px 0',
            gap: '10px'
          }}>
            <hr style={{ flex: 1, border: 'none', borderTop: '1px solid #ddd' }} />
            <span style={{ color: '#666', fontSize: '14px' }}>OR</span>
            <hr style={{ flex: 1, border: 'none', borderTop: '1px solid #ddd' }} />
          </div>
          <GoogleSignInButton text="Sign in with Google" />
        </div>

        {error && <div className="error-message">{error}</div>}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
            />
          </div>

          <button type="submit" disabled={loading} className="btn-primary">
            {loading ? 'Logging in...' : 'Login'}
          </button>

          <div className="form-footer">
            <Link to="/forgot-password" className="forgot-password-link">
              Forgot your password?
            </Link>
          </div>
        </form>

        <p className="auth-link">
          Don't have an account? <Link to="/register">Register here</Link>
        </p>

        {/* ✅ Legal links (open modals, stays on login screen) */}
        <div className="auth-legal-links">
          <span>By using this app you agree to our </span>
          <button
            type="button"
            className="auth-legal-link-btn"
            onClick={() => setOpenModal('terms')}
          >
            Terms of Use
          </button>
          <span className="auth-legal-divider">•</span>
          <button
            type="button"
            className="auth-legal-link-btn"
            onClick={() => setOpenModal('privacy')}
          >
            Privacy Policy
          </button>
        </div>
      </div>

      {/* ✅ Terms modal */}
      {openModal === 'terms' && (
        <Modal title={TERMS_OF_USE.content.title}>
          <p><strong>Version:</strong> {TERMS_OF_USE.version}</p>
          <p><strong>Last updated:</strong> {TERMS_OF_USE.lastUpdated}</p>

          {TERMS_OF_USE.content.sections.map((s) => (
            <div key={s.heading} style={{ marginTop: 14 }}>
              <h4 style={{ margin: '0 0 6px 0' }}>{s.heading}</h4>
              <p style={{ margin: 0 }}>{s.content}</p>
            </div>
          ))}
        </Modal>
      )}

      {/* ✅ Privacy modal */}
      {openModal === 'privacy' && (
        <Modal title={PRIVACY_POLICY.content.title}>
          <p><strong>Version:</strong> {PRIVACY_POLICY.version}</p>
          <p><strong>Last updated:</strong> {PRIVACY_POLICY.lastUpdated}</p>

          {PRIVACY_POLICY.content.sections.map((s) => (
            <div key={s.heading} style={{ marginTop: 14 }}>
              <h4 style={{ margin: '0 0 6px 0' }}>{s.heading}</h4>
              <p style={{ margin: 0 }}>{s.content}</p>
            </div>
          ))}
        </Modal>
      )}

      {/* GDPR Cookie Banner */}
      <CookieConsentBanner />
    </div>
  );
}

export default Login;
